import React from "react";
import { Building2, House as Home, TreePine, FileText, Landmark, BedDouble, UtensilsCrossed, Bath, ArrowLeftRight, Layers, ShieldCheck, Compass, Scale } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface TypologyData {
  title: string;
  era: string;
  structuralTraits: string;
  renovationNeeds: string;
  planningFocus: string;
}
interface CatalystData {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  context: string;
  considerations: string;
}
const typologies: TypologyData[] = [{
  title: "Craftsman Bungalows & Four-Squares",
  era: "1910s – 1930s",
  structuralTraits: "Continuous brick or stone pier foundations, full-dimension dimensional timber framing, low-pitched gables, generous overhanging eaves, and expansive front porches.",
  renovationNeeds: "Older crawl space foundations often require supplemental leveling and modern vapor encapsulation. Reworking original floor plans involves evaluating load-bearing spine walls, matching historic window muntin profiles, and upgrading gravity heating transitions.",
  planningFocus: "Attic conversions (dormer additions) and rear-oriented footprint extensions that protect primary street-facing rooflines and existing architectural scale."
}, {
  title: "Mid-Century Brick Ranches",
  era: "1940s – 1960s",
  structuralTraits: "Linear footprints, low-slope hip or gable roofs, concrete slab or daylight basement foundations, compartmentalized utility cores, and brick veneer exterior envelopes.",
  renovationNeeds: "Original floor plans typically separate kitchen, dining, and living rooms with dividing partitions. Modernization often entails steel beam integrations to open primary living spans, reconfiguring ductwork, and augmenting insulation in exterior wall assemblies.",
  planningFocus: "Vaulting shallow ceilings into cathedral profiles, converting attached carports into conditioned suites, and modernizing thermal envelopes for energy efficiency."
}, {
  title: "Historic Cottages & Folk Victorians",
  era: "Late 1800s – 1910s",
  structuralTraits: "Steeper pitch roof lines, asymmetric facades, decorative vergeboards and bracketed porches, heavy timber or balloon framing, and original plaster-over-lath assemblies.",
  renovationNeeds: "Irregular joist spans and settled corner piers require careful plumb and level remediation before installing heavy kitchen or tile loads. Updating legacy plumbing and wiring runs demands thoughtful chasing without stripping exterior millwork.",
  planningFocus: "Preserving irreplaceable Heart Pine flooring, restoring authentic siding profiles, and harmonizing modern mechanical systems within compact historical footprints."
}];
const catalysts: CatalystData[] = [{
  icon: BedDouble,
  title: "Adding Bedroom & Suite Space",
  context: "Growing households often outgrow standard two- and three-bedroom historical layouts, creating a need for dedicated owner retreats or flexible multi-generational suites.",
  considerations: "Evaluating options between second-story pop-top additions, finished attic dormers, or ground-level rear additions that preserve backyard canopy and setbacks."
}, {
  icon: UtensilsCrossed,
  title: "Reconfiguring Compartmentalized Kitchens",
  context: "Historic kitchens were engineered as isolated service rooms with minimal prep surfaces, insufficient pantry storage, and fragmented workflow triangles.",
  considerations: "Opening wall spans to connect adjoining dining rooms, incorporating functional kitchen islands, and balancing contemporary appliances with period-appropriate cabinetry."
}, {
  icon: Bath,
  title: "Master & Secondary Bathroom Updates",
  context: "Many Decatur residences originally featured a single shared hallway bath with compact fixtures and aging galvanized or cast-iron supply lines.",
  considerations: "Carving out dedicated primary en-suites, introducing double vanities and walk-in tiled showers, and upgrading floor sub-assemblies to prevent water intrusion."
}, {
  icon: ArrowLeftRight,
  title: "Remodeling vs. Relocating in Decatur",
  context: "Given low housing inventory and high affection for Decatur's walkable commercial squares and neighborhood schools, moving often introduces higher transaction frictions.",
  considerations: "Investing capital directly into customizing existing square footage or expanding footprint, thereby maintaining established neighborhood ties and school district continuity."
}];
export default function DecaturHousingContext() {
  return <section id="decaturhousingcontext" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 uppercase tracking-widest text-xs px-3 py-1 font-medium">
            Local Housing &amp; Renovation Context
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-normal tracking-tight text-foreground mb-6">
            Understanding Decatur’s Architectural Fabric &amp; Building Landscape
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Residential renovation in Decatur requires an intimate understanding of the city’s historic building types, jurisdictional regulatory frameworks, and neighborhood-specific preservation guidelines. Every home presents distinctive structural realities and procedural pathways.
          </p>
        </div>

        {/* 1. Architectural Typologies */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                Section 01 / Housing Typologies
              </span>
              <h3 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">
                Decatur’s Dominant Residential Styles
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              From early 20th-century bungalows near Oakhurst to mid-century brick ranches in surrounding neighborhoods, each architectural period demands specific structural engineering and material considerations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {typologies.map((item, index) => <Card key={index} data-index={index} className="bg-card text-card-foreground border border-border/80 shadow-xs hover:shadow-sm transition-all duration-300 flex flex-col justify-between">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Badge variant="outline" className="text-xs font-mono">
                      {item.era}
                    </Badge>
                    <Home className="size-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-xl font-serif font-medium text-foreground leading-snug">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 text-sm text-muted-foreground flex-1">
                  <div>
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Layers className="size-3.5 text-muted-foreground" />
                      Structural Traits
                    </h4>
                    <p className="leading-relaxed">{item.structuralTraits}</p>
                  </div>

                  <Separator className="bg-border/60" />

                  <div>
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Compass className="size-3.5 text-muted-foreground" />
                      Renovation Realities
                    </h4>
                    <p className="leading-relaxed">{item.renovationNeeds}</p>
                  </div>

                  <Separator className="bg-border/60" />

                  <div>
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Building2 className="size-3.5 text-muted-foreground" />
                      Planning &amp; Expansion
                    </h4>
                    <p className="leading-relaxed">{item.planningFocus}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* 2. Jurisdictional Permitting Nuances */}
        <div className="mb-20">
          <div className="mb-8">
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
              Section 02 / Permitting &amp; Jurisdiction
            </span>
            <h3 className="text-2xl sm:text-3xl font-serif font-medium text-foreground mb-3">
              City of Decatur vs. Unincorporated DeKalb County
            </h3>
            <p className="text-muted-foreground max-w-3xl leading-relaxed text-sm sm:text-base">
              Property jurisdiction dictates zoning rules, building plan review times, environmental ordinances, and inspection protocols. Knowing whether a parcel sits inside the incorporated municipal boundaries of the City of Decatur or within unincorporated DeKalb County shapes project feasibility from the outset.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* City of Decatur Card */}
            <Card className="bg-card text-card-foreground border border-border shadow-xs">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                    <Landmark className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-serif font-medium text-foreground">
                      City of Decatur Municipal Jurisdiction
                    </CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Incorporated 4.2 sq. mile city limits (Zip Code 30030)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <TreePine className="size-4 text-muted-foreground" />
                    Tree Canopy Preservation Ordinance
                  </h4>
                  <p className="leading-relaxed">
                    Decatur enforces one of the region’s most comprehensive tree conservation ordinances. Residential additions or heavy site work triggering disturbance must account for tree save areas, critical root zones, and strict canopy replacement calculations or mitigation fees before land disturbance permits are granted.
                  </p>
                </div>

                <Separator className="bg-border/60" />

                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <Scale className="size-4 text-muted-foreground" />
                    Impervious Lot Coverage &amp; Stormwater Limits
                  </h4>
                  <p className="leading-relaxed">
                    Single-family zoning ordinances set defined maximum percentages for total lot coverage by impervious surfaces (including rooflines, driveways, walkways, and patios). Expansions often necessitate on-site stormwater management techniques such as dry wells, rain gardens, or permeable pavers.
                  </p>
                </div>

                <Separator className="bg-border/60" />

                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <FileText className="size-4 text-muted-foreground" />
                    Centralized Municipal Building Inspections
                  </h4>
                  <p className="leading-relaxed">
                    Plan reviews and sequential trade inspections (framing, MEP rough-ins, insulation, and final occupancy) occur directly through the City of Decatur Design, Environment &amp; Construction Division, following distinct municipal schedules.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Unincorporated DeKalb County Card */}
            <Card className="bg-card text-card-foreground border border-border shadow-xs">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                    <Building2 className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-serif font-medium text-foreground">
                      Unincorporated DeKalb County
                    </CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Adjacent unincorporated neighborhoods (30030, 30032, 30033)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <FileText className="size-4 text-muted-foreground" />
                    County Planning &amp; Sustainability Department
                  </h4>
                  <p className="leading-relaxed">
                    Building permits, zoning verifications, and trade applications are submitted through DeKalb County’s central portal. Review cycles reflect county-wide volume and follow DeKalb County residential zoning codes and standard setback tables.
                  </p>
                </div>

                <Separator className="bg-border/60" />

                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <Scale className="size-4 text-muted-foreground" />
                    Zoning Setbacks &amp; Variances
                  </h4>
                  <p className="leading-relaxed">
                    Properties under DeKalb County jurisdiction adhere to county-level zoning classifications (such as R-75 or R-85), which dictate specific front, side, and rear yard building setback minimums. Additions encroaching on standard setbacks require Board of Zoning Appeals review.
                  </p>
                </div>

                <Separator className="bg-border/60" />

                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <ShieldCheck className="size-4 text-muted-foreground" />
                    Environmental Health &amp; Infrastructure
                  </h4>
                  <p className="leading-relaxed">
                    Renovation projects involving plumbing expansions must verify sewer connection availability or DeKalb County Board of Health septic records where older non-sewered parcels remain in certain perimeter sub-districts.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 3. Historic District Design Review */}
        <div className="mb-20">
          <Card className="bg-card text-card-foreground border border-border shadow-xs overflow-hidden">
            <CardHeader className="bg-secondary/40 pb-6 border-b border-border/60">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                    Section 03 / Preservation Standards
                  </span>
                  <CardTitle className="text-2xl sm:text-3xl font-serif font-medium text-foreground">
                    Historic District Design Review &amp; Exterior Guidelines
                  </CardTitle>
                </div>
                <Badge variant="outline" className="self-start sm:self-center font-mono text-xs">
                  COA Review Process
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
              <p>
                Properties situated within locally designated historic districts—such as the <strong className="text-foreground font-medium">MAK Historic District</strong> (McDonough-Adams-Kings Highway), <strong className="text-foreground font-medium">Old Decatur</strong>, <strong className="text-foreground font-medium">Clairemont Avenue</strong>, or <strong className="text-foreground font-medium">Ponce de Leon Court</strong>—are subject to exterior design review by the City of Decatur Historic Preservation Commission (HPC).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                <div className="p-4 rounded-lg bg-background border border-border/80 space-y-2">
                  <h4 className="font-serif font-medium text-foreground text-base">
                    Certificate of Appropriateness (COA)
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Prior to obtaining building permits, proposed exterior modifications visible from the public right-of-way require an approved COA. Minor repairs matching original materials in kind may be handled administratively, while substantive additions or structural changes undergo commission public hearings.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-background border border-border/80 space-y-2">
                  <h4 className="font-serif font-medium text-foreground text-base">
                    Windows &amp; Exterior Millwork
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Historic guidelines emphasize the preservation and repair of original wood windows, siding profiles, and front porch elements over replacement. Where replacement is demonstrated to be structurally necessary, matching material profiles, sash dimensions, and true-divided-lite patterns is required.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-background border border-border/80 space-y-2">
                  <h4 className="font-serif font-medium text-foreground text-base">
                    Additions &amp; Massing Proportion
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    New extensions or second-story additions must be architecturally subordinate to the primary historic structure, stepped back from street elevations, and designed with roof pitches and exterior materials that harmonize with neighborhood context without mimicking historical styles falsely.
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic pt-2">
                Note: Standard interior renovations (such as kitchen or bathroom remodels, interior wall alterations, and mechanical upgrades) that do not alter the exterior envelope do not require HPC review or a Certificate of Appropriateness.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* 4. Remodeling Catalysts & Decision-Making */}
        <div>
          <div className="mb-8">
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
              Section 04 / Homeowner Drivers
            </span>
            <h3 className="text-2xl sm:text-3xl font-serif font-medium text-foreground mb-3">
              Common Situations That Prompt Renovation in Decatur
            </h3>
            <p className="text-muted-foreground max-w-3xl leading-relaxed text-sm sm:text-base">
              Decatur homeowners typically consider major renovations during pivotal life transitions or when legacy floor plans no longer support modern daily routines. Understanding these core catalysts helps clarify project scopes and design priorities.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {catalysts.map((cat, idx) => {
            const IconComp = cat.icon;
            return <Card key={idx} data-index={idx} className="bg-card text-card-foreground border border-border shadow-xs hover:border-border/80 transition-colors flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-md bg-secondary flex items-center justify-center text-foreground mb-3">
                      <IconComp className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-serif font-medium text-foreground leading-snug">
                      {cat.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-xs sm:text-sm text-muted-foreground flex-1">
                    <p className="leading-relaxed">{cat.context}</p>
                    <Separator className="bg-border/50 my-2" />
                    <p className="text-xs text-foreground/80 leading-relaxed font-normal">
                      <strong className="text-foreground font-semibold block mb-1">Key Consideration:</strong>
                      {cat.considerations}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

      </div>
    </section>;
}